import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/i18n/useLocale';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import type { LanguageCode } from '@/config/languages';

/**
 * The one write path for a UI-language choice (#2109 R1 lane L4; research
 * §2.2 rule 8). Settings' `LanguageSelect` and the shell `LanguageSwitcher`
 * both call this so they cannot drift.
 *
 * Anonymous: `setLocale` writes `localStorage['trf.lang']` (LocaleProvider).
 * Signed in: `setLocale` applies the choice at once, then the profile PATCH
 * stores it on `User.preferences.language` (rulings brief B3) so every later
 * visit — on any device — opens in that language. A failed PATCH keeps the
 * choice for this visit and says so; it never reverts the screen.
 *
 * A staff seat (TRFer or admin) is English-only (DECISIONS 2026-09-30) and
 * writes nothing: the person's saved language belongs to their family side.
 */
export function useChooseLanguage(): (code: LanguageCode) => Promise<void> {
  const { user } = useAuth();
  const { setLocale, staffEnglishOnly } = useLocale();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useCallback(
    async (code: LanguageCode) => {
      if (staffEnglishOnly) return;
      await setLocale(code);
      if (!user) return;
      try {
        await api.auth.updateProfile({ language: code });
      } catch {
        toast({
          variant: 'destructive',
          title: t('language.saveFailed'),
          description: t('language.saveFailedDescription'),
        });
      }
    },
    [staffEnglishOnly, user, setLocale, toast, t],
  );
}
